/**
 * Confirm Step Component
 * Shown when all fields are pre-configured (session mode).
 * Restyled with NowRamp theme tokens.
 */

import { useState } from 'react';
import { useRamp } from '../context/RampContext';
import { isStablecoin, formatOutput } from '../data/cryptoData';
import { Spinner } from './ui/Spinner';
import { useT } from '../i18n';

export interface ConfirmStepProps {
  className?: string;
  stablecoinParity?: boolean;
  partnerName?: string;
}

function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
}

export function ConfirmStep({ className = '', stablecoinParity, partnerName }: ConfirmStepProps) {
  const {
    config,
    configLoading,
    state,
    defaultGateway,
    quotes,
    fetchQuotes,
    createOrder,
    orderLoading,
  } = useRamp();
  const t = useT();

  const isSell = state.flowType === 'sell';
  const [error, setError] = useState<string | null>(null);

  const gatewayInfo = config?.gateways.find((g) => g.id === defaultGateway);

  // Parity display for confirm step: stablecoin equivalent of fiat (partner absorbs fees)
  const exchangeRate = quotes?.bestQuote?.exchangeRate ? parseFloat(quotes.bestQuote.exchangeRate) : null;
  const fiatNumeric = parseFloat(state.fiatAmount) || 0;
  const confirmParityActive = !!(stablecoinParity && partnerName && !isSell && isStablecoin(state.cryptoCurrency) && exchangeRate);
  const confirmSamePeg = confirmParityActive && state.cryptoCurrency.toUpperCase().includes(state.fiatCurrency.toUpperCase());
  const confirmParityAmount = confirmParityActive && fiatNumeric > 0
    ? formatOutput(confirmSamePeg ? fiatNumeric : fiatNumeric / exchangeRate)
    : null;

  const handleConfirm = async () => {
    setError(null);
    try {
      fetchQuotes();
      await createOrder();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.createOrderFailed');
      setError(message);
    }
  };

  if (configLoading) {
    return (
      <div className={`flex flex-col items-center justify-center p-12 ${className}`}>
        <Spinner size={32} />
        <p className="text-[14px] mt-4" style={{ color: 'var(--t-text-secondary)' }}>{t('confirm.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center px-8 py-12 ${className}`}>
      <h2 className="text-[20px] font-semibold mb-6" style={{ color: 'var(--t-text)' }}>
        {isSell ? t('confirm.sellTitle') : t('confirm.buyTitle')}
      </h2>

      <div className="w-full text-left mb-6" style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius, 20px)', padding: 16 }}>
        {gatewayInfo && (
          <div className="flex justify-between items-center py-2" style={{ borderBottom: '1px solid var(--t-divider)' }}>
            <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>{t('confirm.provider')}</span>
            <span className="flex items-center gap-2">
              {gatewayInfo.logo && (
                <img src={gatewayInfo.logo} alt={gatewayInfo.name} width={20} height={20} style={{ borderRadius: 4 }} />
              )}
              <span className="text-[13px] font-medium" style={{ color: 'var(--t-text)' }}>{gatewayInfo.name}</span>
            </span>
          </div>
        )}

        {isSell ? (
          <>
            <DetailRow label={t('confirm.youSell')} value={`${state.cryptoAmount} ${state.cryptoCurrency}`} highlight />
            <DetailRow label={t('confirm.youReceive')} value={t('confirm.payout', { currency: state.fiatCurrency })} />
          </>
        ) : (
          <>
            <DetailRow label={t('confirm.youPay')} value={`${state.fiatAmount} ${state.fiatCurrency}`} highlight />
            <DetailRow
              label={t('confirm.youReceive')}
              value={confirmParityAmount ? `${confirmParityAmount} ${state.cryptoCurrency}` : t('confirm.cryptoOnNetwork', { crypto: state.cryptoCurrency, network: state.network })}
            />
            <DetailRow label={t('confirm.wallet')} value={truncateAddress(state.walletAddress)} mono />
            {confirmParityActive && confirmParityAmount && (
              <div className="py-2 text-[11px]" style={{ color: 'var(--t-text-muted)', lineHeight: '15px' }}>
                {t('confirm.parityNote', { amount: confirmParityAmount, crypto: state.cryptoCurrency, partner: partnerName })}
              </div>
            )}
          </>
        )}

        <DetailRow label={t('confirm.network')} value={state.network} />

        {isSell && state.walletAddress && (
          <DetailRow label={t('confirm.refundAddress')} value={truncateAddress(state.walletAddress)} mono />
        )}
      </div>

      {error && (
        <p className="text-[12px] mb-4" style={{ color: '#EF4444' }}>{error}</p>
      )}

      <button
        className="w-full font-semibold text-[16px] transition-all flex items-center justify-center gap-2"
        style={{ height: 60, borderRadius: 'var(--t-cta-radius, 100px)', background: 'var(--t-cta-bg)', color: 'var(--t-cta-text)', opacity: orderLoading ? 0.7 : 1 }}
        disabled={orderLoading}
        onClick={handleConfirm}
      >
        {orderLoading ? (
          <>
            <Spinner size={18} />
            {t('confirm.processing')}
          </>
        ) : (
          isSell ? t('confirm.confirmSell') : t('confirm.confirmPay')
        )}
      </button>
    </div>
  );
}

function DetailRow({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center py-2" style={{ borderBottom: '1px solid var(--t-divider)' }}>
      <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>{label}</span>
      <span
        className={`text-[13px] font-medium ${mono ? 'font-mono' : ''}`}
        style={{ color: highlight ? 'var(--t-success, #10b981)' : 'var(--t-text)' }}
      >
        {value}
      </span>
    </div>
  );
}
